import Chart from 'react-apexcharts';
import React, { useEffect, useState } from 'react';
import { reportingApi } from 'src/api/reporting';
import { toast } from 'react-hot-toast';
import hexToRgbA from 'src/utils/hexToRgbA';
import { Box } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

type BillingAccount = {
  id: string;
  name: string;
};

interface AccountWiseHourComparisonChartProps {
  selectedOrganization: {
    id: string;
    reportingLowerLimit?: number;
    reportingUpperLimit?: number;
  };
  billingAccountId: BillingAccount[];
}

const CHART_COLORS = [
  '#003f5c',
  '#374c80',
  '#7a5195',
  '#bc5090',
  '#ef5675',
  '#ff764a',
  '#ffa600',
];

const AccountWiseHourComparisonChart = ({
  selectedOrganization,
  billingAccountId,
}: AccountWiseHourComparisonChartProps) => {
  const [accountWiseHourData, setAccountWiseHourData] = useState<any>({
    series: [
      {
        name: 'No Data',
        data: [0],
      },
    ],
    options: {
      colors: CHART_COLORS,
      tooltip: {
        y: {
          formatter: (value) => value?.toFixed(2),
        },
      },
      grid: {
        strokeDashArray: 7,
        show: true,
        xaxis: {
          lines: {
            show: false,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
      },
      chart: {
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
        stacked: true,
      },
      stroke: {
        width: 0,
        colors: ['transparent'],
      },
      markers: {
        size: 4,
      },
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        categories: ['No Data'],
        axisBorder: {
          show: false,
        },
        title: {
          text: 'Logged Hours (per account)',
          offsetY: -10,
          style: {
            fontFamily: 'Quicksand',
            fontSize: '14px',
            fontWeight: 500,
            color: '#7A7A9D',
          },
        },
        labels: {
          rotate: 0,
          formatter: (value) => value,
          style: {
            fontFamily: 'Quicksand',
            fontSize: '12px',
            fontWeight: 400,
            colors: '#7A7A9D',
          },
        },
        axisTicks: { show: false },
      },
      legend: {
        show: true,
        position: 'top',
        fontSize: '12px',
        fontFamily: 'Quicksand',
        fontWeight: 500,
        markers: {
          width: 16,
          height: 16,
          radius: 4,
        },
        itemMargin: {
          horizontal: 10,
          vertical: 5,
        },
        horizontalAlign: 'center',
      },
      yaxis: {
        title: {
          text: '',
          style: {
            fontFamily: 'Quicksand',
            fontSize: '14px',
            fontWeight: 500,
            color: '#7A7A9D',
          },
        },
        labels: {
          formatter: (value) => value?.toFixed(2),
          style: {
            fontFamily: 'Quicksand',
            fontSize: '12px',
            fontWeight: 400,
            colors: '#7A7A9D',
          },
        },
        min: selectedOrganization?.reportingLowerLimit ?? undefined,
        max: selectedOrganization?.reportingUpperLimit ?? undefined,
        lines: {
          show: true,
          colors: ['#FF0000', '#FF0000'],
          opacity: 0.3,
          value: [
            selectedOrganization?.reportingLowerLimit,
            selectedOrganization?.reportingUpperLimit,
          ].filter(Boolean),
        },
      },
      fill: {
        opacity: 1,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '70%',
        },
      },
    },
  });

  const [loading, setLoading] = useState(false);

  const processDataForAccountWiseSprint = (data) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return {
        series: [
          {
            name: 'No Data',
            data: [0],
          },
        ],
        options: {
          ...accountWiseHourData.options,
          xaxis: {
            ...accountWiseHourData.options.xaxis,
            categories: ['No Data'],
          },
        },
      };
    }

    const allAccounts = new Set();
    data.forEach((sprint) => {
      if (sprint?.accounts) {
        sprint.accounts.forEach((account) => {
          if (account?.accountName) {
            allAccounts.add(account.accountName);
          }
        });
      }
    });

    const chartColors = CHART_COLORS.slice(0, allAccounts.size);

    return {
      series: Array.from(allAccounts).map((accountName) => ({
        name: accountName,
        data: data.map((sprint) => {
          if (!sprint?.accounts) {
            return null;
          }
          const account = sprint.accounts.find(
            (acc) => acc?.accountName === accountName
          );
          return account?.totalLoggedTimeInHours || null;
        }),
      })),
      options: {
        ...accountWiseHourData.options,
        colors: chartColors,
        tooltip: {
          y: {
            formatter: (value) => (value ? value.toFixed(2) : value),
          },
          intersect: false,
          shared: true,
        },
        xaxis: {
          categories: data.map((item) => item?.sprintName.split(' ') || ''),
          axisBorder: {
            show: false,
          },
          title: {
            text: 'Logged Hours (per account)',
            offsetY: 0,
            style: {
              fontFamily: 'Quicksand',
              fontSize: '14px',
              fontWeight: 500,
              color: '#7A7A9D',
            },
          },
          labels: {
            rotate: 0,
            formatter: (value) => value,
            style: {
              fontFamily: 'Quicksand',
              fontSize: '12px',
              fontWeight: 400,
              colors: '#7A7A9D',
            },
          },
          axisTicks: { show: false },
        },
        yaxis: {
          ...accountWiseHourData.options.yaxis,
          min: selectedOrganization?.reportingLowerLimit ?? undefined,
          max: selectedOrganization?.reportingUpperLimit ?? undefined,
          lines: {
            show: true,
            colors: ['#FF0000', '#FF0000'],
            opacity: 0.3,
            value: [
              selectedOrganization?.reportingLowerLimit,
              selectedOrganization?.reportingUpperLimit,
            ].filter(Boolean),
          },
        },
        dataLabels: {
          enabled: false,
          formatter(val) {
            return val ? val.toFixed(1) : ''; // Show one decimal place, hide if 0
          },
          style: {
            fontSize: '10px',
            fontFamily: 'Quicksand',
            fontWeight: 'bold',
            // colors: ['#fff'], // White text color
          },
          // Only show labels for values that would fit in the bar
          hideOverflowingLabels: true,
        },
        stroke: {
          width: 1,
          colors: ['transparent'],
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '70%',
            borderRadiusApplication: 'around',
            borderRadiusWhenStacked: 'all',
            borderRadius: 2,
            distributed: false,
            dataLabels: {
              position: 'center',
            },
            shadowColor: 'rgba(0,0,0,0.1)',
            shadowBlur: 3,
            shadowOffsetX: 2,
            shadowOffsetY: 2,
          },
        },
        states: {
          hover: {
            filter: {
              type: 'lighten',
              value: 0.15,
            },
          },
          active: {
            allowMultipleDataPointsSelection: false,
            filter: {
              type: 'darken',
              value: 0.35,
            },
          },
        },
      },
    };
  };

  const fetchAccountWiseChartData = async () => {
    if (!selectedOrganization?.id) {
      return;
    }

    setLoading(true);

    try {
      const response = await reportingApi.getAccountWiseHourComparisonData({
        orgId: selectedOrganization.id,
        billingAccountId:
          billingAccountId?.length > 0
            ? billingAccountId.map((account) => account.id).join(',')
            : null,
      });

      if (response?.data) {
        setAccountWiseHourData(
          processDataForAccountWiseSprint([...response.data].reverse())
        );
      }
    } catch (error) {
      toast.error('Error fetching chart data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Reset chart data when organization changes
    setAccountWiseHourData({
      series: [
        {
          name: 'No Data',
          data: [0],
        },
      ],
      options: {
        ...accountWiseHourData.options,
        xaxis: {
          ...accountWiseHourData.options.xaxis,
          categories: ['No Data'],
        },
      },
    });

    fetchAccountWiseChartData();
  }, [selectedOrganization?.id, billingAccountId]);

  return (
    <>
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%">
          <CircularProgress />
        </Box>
      ) : (
        <Chart
          options={accountWiseHourData.options}
          series={accountWiseHourData.series}
          type="bar"
          height={350}
        />
      )}
    </>
  );
};

export default AccountWiseHourComparisonChart;
